import { FunctionComponent, ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { Tooltip } from "@digital-services/ui";

import ErrorComponent from "@components/ErrorComponent";
import LoadingComponent from "@components/LoadingComponent";
import SectionTitle from "@components/SectionTitle";

import { ApplicationJsonItemResponse } from "@applications/models/ApplicationJsonModel";
import { useGetAssociatedApplications } from "@applications/services/ApplicationService";

import { QuickAccessTile } from "./QuickAccessTile";

const ApplicationsPanel: FunctionComponent = () => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "dashboard.applications"
  });
  const { t: tApplications } = useTranslation(undefined, {
    keyPrefix: "organization.applications"
  });

  const { applications, error, isLoading } = useGetAssociatedApplications();

  const renderChildren = useCallback(
    (children: ReactNode): ReactNode => {
      return (
        <div className="flex flex-col">
          <SectionTitle title={t("section_title")} />
          <div className="flex gap-10">{children}</div>
        </div>
      );
    },
    [t]
  );

  if (isLoading)
    return <LoadingComponent message={tApplications("load.message")} />;
  if (error)
    return <ErrorComponent message={tApplications("load.error.message")} />;

  if (applications?.length === 0)
    return (
      <div className="flex py-10">
        <ErrorComponent message={tApplications("load.none.message")} />
      </div>
    );

  if (applications)
    return renderChildren(
      <div
        className="mr-6 flex flex-wrap gap-4"
        data-testid="applicationsPanel"
      >
        {applications.map((appResponse: ApplicationJsonItemResponse) => {
          const appData = appResponse.data; // yes, .data will be always present
          const appError = appResponse?.error;

          const keyValue = `application-item-on-applications-panel-${appData.id}`;

          if (appError) {
            return (
              <Tooltip
                key={keyValue}
                message={appError}
                placement="bottom-left"
              >
                <QuickAccessTile
                  application={appData}
                  target="_blank"
                  className="border-2 border-red-500"
                />
              </Tooltip>
            );
          }

          return (
            <QuickAccessTile
              key={keyValue}
              application={appData}
              target="_blank"
            />
          );
        })}
      </div>
    );
};

export default ApplicationsPanel;
